import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";

const featuredIds = ["m3", "t1", "m1", "t4", "t7"];

export default function HeroCarousel() {
  const featured = useMemo(
    () => featuredIds.map((id) => movies.find((m) => m.id === id)).filter(Boolean),
    []
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (featured.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [featured.length]);

  const goPrev = () => setIndex((prev) => (prev - 1 + featured.length) % featured.length);
  const goNext = () => setIndex((prev) => (prev + 1) % featured.length);

  return (
    <section className="relative h-[420px] w-full overflow-hidden bg-primary-950 md:h-[600px]">
      {featured.map((movie, i) =>
        movie ? (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="absolute inset-0 h-full w-full">
              <img
                src={movie.backdrop}
                alt={movie.title}
                title={`${movie.title} - 影视资讯焦点推荐`}
                className={`h-full w-full object-cover object-center ${
                  i === index ? "animate-kenburns" : ""
                }`}
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30" />
          </div>
        ) : null
      )}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-14 md:px-6 md:pb-20">
        <div className="max-w-2xl">
          <div className="mb-3 flex items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-background-50">
              焦点推荐
            </span>
            {featured[index] && (
              <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-100 ring-1 ring-background-50/30">
                {featured[index]!.type === "movie" ? "电影" : "电视剧"} ·{" "}
                {featured[index]!.rating.toFixed(1)} 分
              </span>
            )}
          </div>
          <h1 className="text-2xl font-bold leading-tight text-background-50 md:text-4xl lg:text-5xl">
            {featured[index]?.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {featured[index]?.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-md border border-background-50/30 bg-background-50/10 px-2.5 py-1 text-xs text-background-100 whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
          <p className="mt-4 hidden max-w-xl text-sm leading-relaxed text-background-200 sm:block md:text-base">
            {featured[index]?.summary}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${featured[index]?.id}`}
              className="flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg" />
              查看剧情解析
            </Link>
            <Link
              to={`/search?q=${encodeURIComponent(featured[index]?.title ?? "")}`}
              className="flex items-center gap-2 rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line text-lg" />
              搜索相关资讯
            </Link>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-4">
          <div className="flex items-center gap-2">
            {featured.map((movie, i) => (
              <button
                key={movie?.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`切换到 ${movie?.title}`}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={goPrev}
              aria-label="上一部"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-50/30 bg-background-50/10 text-background-50 transition-colors hover:bg-background-50/20 cursor-pointer md:h-10 md:w-10"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="下一部"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-50/30 bg-background-50/10 text-background-50 transition-colors hover:bg-background-50/20 cursor-pointer md:h-10 md:w-10"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}